import './styles/App.css';

import React, { useEffect, useState } from 'react';

import ApolloClientService from '../shared/services/ApolloClientService';
import { ApolloProvider } from '@apollo/client';
import AuthService from '../shared/services/AuthService';
import Button from '@mui/material/Button';
import Config from '../shared/config';
import FeatureAccessService from '../shared/services/FeatureAccessService';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import { Notice } from './models/Notice';
import { NoticeReceiver } from './models/NoticeReceiver';
import NotificationService from './services/NotificationService';
import NotificationSettingsDialog from './components/NotificationSettingsDialog';
import { ReceivedNotifications } from './components/ReceivedNotifications';
import SearchTextField from '../shared/components/SearchTextField';
import { SentNotifications } from './components/SentNotifications';
import SettingsIcon from '@mui/icons-material/Settings';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { UpdateAllNotificationsAction } from './gql-types';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import useNotifications from './hooks/useNotifications';

enum NotificationTab {
  received,
  sent
}

interface NotificationAppProps {
  receivedNotifications: NoticeReceiver[];
  sentNotifications: Notice[];
}

const App = (props: NotificationAppProps) => {
  const graphQLQueryEndpoint = Config.DASHBOARD_GQL;
  const apolloClient = ApolloClientService.create(graphQLQueryEndpoint);
  const showSentTab = FeatureAccessService.hasGroupAccess;

  const [tab, setTab] = React.useState(0);

  const [receivedText, setReceivedText] = useState<string>('');
  const [sentText, setSentText] = useState<string>('');
  const [showNotificationSettings, setShowNotificationSettings] =
    useState(false);

  const userkey = AuthService.Userkey;
  const location = useLocation();
  const { updateAllNotifications } = useNotifications(
    AuthService.Userkey,
    false /* loading data not required here */
  );

  useEffect(() => {
    if (location.pathname === '/notifications/settings') {
      openNotificationSettings();
    }
  }, [location.pathname]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  function openNotificationSettings() {
    setShowNotificationSettings(true);
  }

  function closeNotificationSettings() {
    setShowNotificationSettings(false);
  }

  const handleUpdateAllNotifications = (
    action: UpdateAllNotificationsAction
  ) => {
    updateAllNotifications(userkey, action);
  };

  return (
    <ApolloProvider client={apolloClient}>
      <div className="notifications-container">
        <div className="notifications-body">
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <Tabs value={tab} onChange={handleChange}>
                <Tab label="Received" />
                {showSentTab && <Tab label="Sent" />}
              </Tabs>
            </Grid>
            <Grid>
              <IconButton size="small" onClick={openNotificationSettings}>
                <SettingsIcon />
              </IconButton>
            </Grid>
          </Grid>
          {tab === NotificationTab.received && (
            <Grid container direction="column">
              <Grid container spacing={2} alignItems="center">
                <Grid>
                  <SearchTextField
                    disabled={isEmpty(props.receivedNotifications)}
                    trigger="type"
                    onSearch={setReceivedText}
                    searchText={receivedText}
                  />
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      isEmpty(props.receivedNotifications) ||
                      !props.receivedNotifications.some((n) => !n.read)
                    }
                    onClick={() => handleUpdateAllNotifications('read')}
                  >
                    Mark All as Read
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isEmpty(props.receivedNotifications)}
                    onClick={() => handleUpdateAllNotifications('hidden')}
                  >
                    Dismiss All
                  </Button>
                </Grid>
              </Grid>
              <Grid>
                <ReceivedNotifications
                  notifications={
                    NotificationService.search(
                      props.receivedNotifications || [],
                      receivedText
                    ) as NoticeReceiver[]
                  }
                />
              </Grid>
            </Grid>
          )}
          {tab === NotificationTab.sent && (
            <>
              <SearchTextField
                disabled={isEmpty(props.sentNotifications)}
                trigger="type"
                onSearch={setSentText}
                searchText={sentText}
              />
              <SentNotifications
                notifications={
                  NotificationService.search(
                    props.sentNotifications || [],
                    sentText
                  ) as Notice[]
                }
              />
            </>
          )}
        </div>
        {showNotificationSettings && (
          <NotificationSettingsDialog
            open={showNotificationSettings}
            userkey={userkey}
            onClose={closeNotificationSettings}
          />
        )}
      </div>
    </ApolloProvider>
  );
};

export default App;
